/**
 * The lower-cased words a frequency tool counts.
 *
 * The word counter's top-ten list, the word frequency table, the phrase counter and the
 * lexical diversity calculator all cut text into words the same way, so a word has one
 * spelling across them: letters from any script, combining marks (so "café" written with
 * a separate accent still hangs together), digits, and an apostrophe — straight or
 * curly — between two letters, so "don’t" is one word and is counted with "don't".
 *
 * Two choices differ by tool and are options rather than separate regexes at each call
 * site:
 *
 * - `hyphens: "keep"` leaves "well-known" whole, which is what the word counter and the
 *   word frequency counter say they do; `"split"` cuts it into two words, which is what
 *   the phrase counter and the lexical diversity calculator say they do.
 * - `groupedNumbers` keeps "10,000" and "3.5" as one word instead of splitting them at
 *   the comma or point.
 *
 * The tools' own whitespace-based word totals do not use this: the word counter's
 * headline count is "anything between spaces" on purpose, and says so.
 */

export interface TokenOptions {
  /** Keep a hyphen between two letters inside the word (default), or cut there. */
  hyphens?: "keep" | "split";
  /** Keep digits joined by "," or "." together (default false). */
  groupedNumbers?: boolean;
}

const NUMBER = String.raw`\p{N}+(?:[.,]\p{N}+)+`;
const WORD_KEEP = String.raw`[\p{L}\p{M}\p{N}]+(?:['’ʼ‐‑-][\p{L}\p{M}\p{N}]+)*`;
const WORD_SPLIT = String.raw`[\p{L}\p{M}\p{N}]+(?:['’ʼ][\p{L}\p{M}\p{N}]+)*`;

const RE_KEEP = new RegExp(WORD_KEEP, "gu");
const RE_SPLIT = new RegExp(WORD_SPLIT, "gu");
const RE_KEEP_NUMBERS = new RegExp(`${NUMBER}|${WORD_KEEP}`, "gu");
const RE_SPLIT_NUMBERS = new RegExp(`${NUMBER}|${WORD_SPLIT}`, "gu");
const RE_CURLY_APOSTROPHE = /[’ʼ]/g;

function patternFor(options: TokenOptions): RegExp {
  const split = options.hyphens === "split";
  if (options.groupedNumbers) {
    return split ? RE_SPLIT_NUMBERS : RE_KEEP_NUMBERS;
  }
  return split ? RE_SPLIT : RE_KEEP;
}

/**
 * Words in reading order, lower-cased, NFC-normalised, with curly apostrophes made
 * straight. Punctuation, symbols and web-address separators are not part of a word.
 */
export function lowercaseWords(
  text: string,
  options: TokenOptions = {}
): string[] {
  const normalized = text.normalize("NFC").toLowerCase();
  const words = normalized.match(patternFor(options)) ?? [];
  return words.map((word) => word.replace(RE_CURLY_APOSTROPHE, "'"));
}

/** Length in characters a reader sees (code points), for "drop one-letter words". */
export function wordLength(word: string): number {
  return Array.from(word).length;
}
